// Copyright (c) StrangeBee 2023

import { ICONS } from '@Constants/iconsApp';
import { THVisualizer } from '@Molecules/THVisualizer/THVisualizer';
import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import { PageInput, PageOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/locale';
import DeleteButton from '@Atoms/Buttons/DeleteButton/DeleteButton';
import THTag from '@Atoms/THTag/THTag';
import { HTMLColors } from '@Constants/colors';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { onCaseImageUpload, onOrganisationImageUpload } from '@Helpers/download';
import { useParams } from 'react-router-dom';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';

import './MarkdownVisualizer.scss';

interface MarkdownVisualizerProps {
    onEdit: (page: Partial<PageInput>, pageId: string) => any;
    CreateButton: (...args: any[]) => any;
    onDelete: (pageId: string) => any;
    list: PageOutput[];
    modalVisible?: boolean;
    className?: string;
    hasPermissions?: boolean;
    deleteLabel?: string;
}

export const MarkdownVisualizer = ({
    onEdit,
    CreateButton,
    onDelete,
    list,
    modalVisible,
    className,
    hasPermissions = false,
    deleteLabel,
}: MarkdownVisualizerProps): ReactElement => {
    const i = useLocalMessage();

    const { caseId } = useParams<{ caseId: string }>();

    const [selected, setSelected] = useState<PageOutput>();
    const [editMode, setEditMode] = useState(false);
    const [content, setContent] = useState('');
    const [title, setTitle] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [category, setCategory] = useState('');

    useEffect(() => {
        if (!modalVisible) {
            setEditMode(false);
        }
    }, [modalVisible]); //eslint-disable-line

    useEffect(() => {
        if (editMode && selected) {
            setContent(selected.content || '');
            setTitle(selected.title || '');
            setCategory(selected.category || '');
        }
    }, [editMode, selected, list]);

    useEffect(() => {
        if (!selected) {
            setSelected(list.find(Boolean));
        } else {
            setSelected(list.find(({ _id }) => _id === selected._id));
        }
    }, [list, selected]);

    const dataSource = useMemo(
        () =>
            list?.reduce<Record<string, PageOutput[]>>((acc, currValue) => {
                const search = `${currValue.title.toLowerCase()} ${currValue.content.toLocaleLowerCase()}`;
                if (!search.includes(searchFilter.toLowerCase())) {
                    return acc;
                }

                return {
                    ...acc,
                    [currValue.category || 'default']: [...(acc[currValue.category || 'default'] || []), currValue],
                };
            }, {}),
        [list, searchFilter],
    );

    const allCategories = useMemo(() => Object.keys(dataSource), [dataSource]);

    return (
        <THVisualizer<PageOutput>
            className={className}
            itemKey="_id"
            title={
                <>
                    <div className="markdown-visualizer-view__visualizer__header__title">
                        {editMode && hasPermissions ? (
                            <div className="header-title__editable">
                                <THInput value={title} onChange={(event): void => setTitle(event.target.value)} />
                                <THAutoComplete
                                    options={allCategories.map((value) => ({ label: value, value }))}
                                    value={category}
                                    onChange={(value): void => setCategory(value)}
                                />
                            </div>
                        ) : (
                            <>
                                <span className="visualizer-header-title__label">{selected?.title}</span>
                                {selected && <THTag value={selected?.category} full color={HTMLColors.YELLOW} />}
                            </>
                        )}
                    </div>
                    {hasPermissions && (
                        <div className="markdown-visualizer-view__visualizer__header__actions">
                            {!editMode && !!Object.keys(dataSource).length && (
                                <THButton
                                    onClick={(): void => {
                                        setEditMode(true);
                                    }}
                                    size="small"
                                >
                                    <ICONS.EditIcon />
                                </THButton>
                            )}
                            {editMode && (
                                <THButton
                                    onClick={(): void => {
                                        if (selected) {
                                            onEdit({ title, category, content }, selected?._id);
                                        }
                                        setEditMode(false);
                                    }}
                                    disabled={!Boolean(title)}
                                    size="small"
                                    type="primary"
                                >
                                    <ICONS.SaveOutlined />
                                </THButton>
                            )}
                            {editMode && (
                                <THButton onClick={(): void => setEditMode(false)} size="small">
                                    <ICONS.CloseIcon />
                                </THButton>
                            )}
                        </div>
                    )}
                </>
            }
            filter={
                <>
                    {!editMode && hasPermissions && (
                        <CreateButton
                            allCategories={allCategories}
                            className="markdown-visualizer-view__visualizer__header__filter__add-button"
                        />
                    )}
                    <div className="markdown-visualizer-view__visualizer__header__filter">
                        <THInput
                            className="filter-input"
                            value={searchFilter}
                            placeholder={i('page.view.search')}
                            onChange={(event): void => setSearchFilter(event.target.value)}
                        />
                    </div>
                </>
            }
            dataSource={editMode && selected ? [selected] : dataSource}
            content={
                editMode && hasPermissions ? (
                    <THMarkdown
                        key="edit"
                        value={content}
                        onChange={(value) => setContent(value)}
                        onImageUpload={caseId ? onCaseImageUpload(caseId) : onOrganisationImageUpload}
                    />
                ) : (
                    <THMarkdown key="view" value={selected?.content} disabled borderless />
                )
            }
            selectedState={{ selected, setSelected }}
        >
            {(item): ReactElement => (
                <div className="markdown-visualizer-view__visualizer-item">
                    {item.title}

                    {hasPermissions && (
                        <DeleteButton
                            label={deleteLabel || i('page.view.delete')}
                            className="delete-button"
                            onClick={(event): void => event.stopPropagation()}
                            onConfirm={(e): void => {
                                onDelete(item._id);
                                e?.stopPropagation();
                                setSelected(undefined);
                            }}
                        />
                    )}
                </div>
            )}
        </THVisualizer>
    );
};
